import { DocumentJson } from '@xapi/xapi';
import { ControllerHelper, LanguageSlidesDownloadDto, SlideBackgroundConfigurationDownloadDto, SlideDownloadDto, SlideShowDownloadDto, SlideTemplateDownloadDto, SlideshowController } from 'collaboration-service';
import { Button, Icon, Theme, Tooltip, belowBreakpointOrEqual, fadeColor, useBelowBreakpoint, useThemePart } from 'imaginarity-react-ui';
import _ from 'lodash';
import * as React from 'react';
import { useSelector } from 'react-redux';
import { Swipeable, defineSwipe } from "react-touch";
import { ApplicationState } from 'services/ApplicationState/ApplicationState';
import { shallowCompare } from 'services/Helpers';
import { getTranslated } from 'services/Helpers/TranslationHelpers';
import { ImgXapi } from 'services/Helpers/XapiHelpers';
import { ThemeContext } from 'styled-components';
import Slide from './Slide';
import { SlideshowSC as T } from './SlideshowSC';
import { useImgI18N } from 'services/ImgI18N';

export function calculateSlideBackgroundConfigurationStyle(viewMode: SlideViewMode, width: number, height: number, background?: SlideBackgroundConfigurationDownloadDto) {
    const bgStyle: React.CSSProperties = {};
    if (background) {
        const consec = (what: keyof React.CSSProperties, append: string) => {
            bgStyle[what] = (bgStyle[what] ? bgStyle[what] + `, ${append}` : append) as any;
        }
        const cW = (value?: number) => {
            if (!value)
                return value;
            return value * width * 0.01;
        }
        const cH = (value?: number) => {
            if (!value)
                return value;
            return value * height * 0.01;
        }
        const cMin = (value?: number) => {
            if (width < height)
                return cW(value);
            return cH(value);
        }
        const cMax = (value?: number) => {
            if (width > height)
                return cW(value);
            return cH(value);
        }
        const b = background;
        if (b.backgroundColor)
            bgStyle.backgroundColor = b.backgroundColor;
        if (b.foregroundColor)
            bgStyle.color = b.foregroundColor;
        _.forEachRight(b.backgrounds, (bg, i) => {
            consec("backgroundImage", `url(${bg.background.links[0].uri})`);
            consec("backgroundRepeat", "no-repeat");
            let posX = "";
            if (bg.xPosition.start !== undefined && bg.xPosition.start !== null)
                posX = `left ${bg.xPosition.start}%`;
            else if (bg.xPosition.end !== undefined && bg.xPosition.end !== null)
                posX = `right ${bg.xPosition.end}%`;
            let posY = "";
            if (bg.yPosition.start !== undefined && bg.yPosition.start !== null)
                posY = `top ${bg.yPosition.start}%`;
            else if (bg.yPosition.end !== undefined && bg.yPosition.end !== null)
                posY = `bottom ${bg.yPosition.end}%`;
            consec("backgroundPosition", `${posX} ${posY}`);
            switch (bg.sizingMethod) {
                case "MinOfWidthAndHeightDependent":
                    consec("backgroundSize", `${cMin(bg.size?.width)}px ${cMin(bg.size?.height)}px`);
                    break;
                case "MaxOfWidthAndHeightDependent":
                    consec("backgroundSize", `${cMax(bg.size?.width)}px ${cMax(bg.size?.height)}px`);
                    break;
                case "WidthDepenent":
                    consec("backgroundSize", `${cW(bg.size?.width)}px ${cW(bg.size?.height)}px`);
                    break;
                case "HeightDependent":
                    consec("backgroundSize", `${cH(bg.size?.width)}px ${cH(bg.size?.height)}px`);
                    break;
                case "SizeDependent":
                    consec("backgroundSize", `${cW(bg.size?.width)}px ${cH(bg.size?.height)}px`);
                    break;
                case "Covering":
                    consec("backgroundSize", 'cover');
                    break;
                case "Stretching":
                    consec("backgroundSize", '100% 100%');
                    break;
            }
        });
    }
    switch (viewMode) {
        case "Mobile":
            bgStyle.fontSize = width * 0.03;
            break;
        default:
            bgStyle.fontSize = width * 0.013;
            break;
    }
    bgStyle.lineHeight = (bgStyle.fontSize * 1.41) + "px";
    if (viewMode !== "Mobile")
        bgStyle.position = "absolute";
    return bgStyle;
}

export type SlideViewMode = "Tablet vertical" | "Tablet horizontal" | "Desktop" | "Mobile";
export interface SlideContainerDimensions {
    width: number;
    height: number;
    marginTop: number;
    marginLeft: number;
    viewMode: SlideViewMode;
}
const getViewMode = (width: number, height: number): SlideViewMode => {
    if (width >= 1280)
        return "Desktop";
    else if (width > 916)
        return "Tablet horizontal";
    else if (width >= 688)
        return "Tablet vertical";
    else
        return "Mobile";
}

export const calcAvailableSize = (width: number, height: number, full?: boolean): SlideContainerDimensions => {
    var vm = getViewMode(width, height);
    let h = height;
    let w = width;
    const aspect = (a: number) => {
        if (w / a > h)
            w = h * a;
        else {
            h = w / a;
        }
    }
    switch (vm) {
        case "Desktop":
            if (!full) {
                w = w * 0.8;
                aspect(16 / 9);
            }
            break;
        case "Tablet horizontal":
            break;
        case "Tablet vertical":
            break;
        case "Mobile":
            break;
    }
    return { viewMode: vm, width: w, height: h, marginTop: (height - h) * 0.5, marginLeft: (width - w) * 0.5 };
}
const elementResizeDetectorMaker = require("element-resize-detector");
const erdUltraFast = elementResizeDetectorMaker({
    strategy: "scroll" //<- For ultra performance.
});

const mapper = (state: ApplicationState) => ({
    user: state.user,
});

interface SlideShowContentProps {
    slideShow: SlideShowDownloadDto;
    onSlideChange?: (fromId: string | undefined, toId: string) => void;
    lng: string;
    tokenId?: string;
    slideWidth?: number;
}

const SlideShowContent = (p: SlideShowContentProps) => {
    const { slideShow, onSlideChange, lng, tokenId, slideWidth } = p;
    const { t } = useImgI18N("slideshow");
    const slideShowId = slideShow.id;
    const cW = useThemePart((t: Theme) => t.content.width);
    const contentWidth = slideWidth ?? cW;
    const contentHeight = useThemePart((t: Theme) => t.content.height);

    const { user } = useSelector(mapper, shallowCompare);

    const activityId = document.location.href;

    const [stop, setStop] = React.useState<boolean>(false);
    const [cWidth, setCWidth] = React.useState<number>(0);
    const [cHeight, setCHeight] = React.useState<number>(0);
    const [width, setWidth] = React.useState<number>(contentWidth);
    const [height, setHeight] = React.useState<number>(contentHeight);
    const [viewMode, setViewMode] = React.useState<SlideViewMode>('Desktop');
    const [slides, setSlides] = React.useState<SlideDownloadDto[]>([]);
    const [slide, setSlide] = React.useState<SlideDownloadDto>();
    const [templates, setTemplates] = React.useState<SlideTemplateDownloadDto[]>([]);
    const [xapi, setXapi] = React.useState<ImgXapi>();
    const [allowPointerEvents, setAllowPointerEvents] = React.useState<boolean>(false);

    const theme = React.useContext(ThemeContext);
    const isMobileView = belowBreakpointOrEqual({ theme }, "tablet");
    const isMobile = useBelowBreakpoint("tablet");

    React.useEffect(() => {
        setAllowPointerEvents(false);
    }, [slide]);

    React.useEffect(() => {
        const getData = async (xapi: ImgXapi) => {
            // const statements = await xapi.getXapiStatements();
            // if (statements)
            //     console.log('statements', statements);
            // const states = await xapi.getXapiStates(activityId);
            // if (states)
            //     console.log('states', states);


        };
        if (user) {
            const xapi = ImgXapi.getInstance(user);
            setXapi(xapi);
            getData(xapi);
        }
    }, [user, activityId]);

    const setContainerRef = React.useMemo(() => (contextRef: any) => {
        if (contextRef)
            erdUltraFast.listenTo({}, contextRef, (ele: HTMLElement) => {
                setCWidth(ele.offsetWidth);
                setCHeight(ele.offsetHeight);
            });
    }, []);

    const resize = React.useMemo(() => (width: number, height: number) => {
        let viewMode = getViewMode(width, height);
        setWidth(width);
        setHeight(height);
        setViewMode(viewMode);
    }, []);

    const slideChanged = React.useMemo(() => (idx: number, ignoreLast?: boolean) => {
        if (onSlideChange && slides && slides[idx]) {
            const last = idx >= 0 ? slides[idx] : undefined;
            onSlideChange(!ignoreLast ? last?.id : undefined, slides[idx].id);
        }
    }, [onSlideChange, slides]);

    React.useEffect(() => {
        const loadTemplates = async () => {
            const res = await ControllerHelper.singleCall({}, SlideshowController.GetTemplates, true);
            if (res)
                setTemplates(res);
        };

        resize(width, height);
        if (slides.length === 0 && !stop) {
            //console.log("SlideShowContent.tsx::236 => slideShow", slideShow);

            let translated: LanguageSlidesDownloadDto | undefined = getTranslated(slideShow.languageSlides, lng);
            //console.log("SlideShowContent.tsx::239 => translated", translated);

            if (translated && translated.slides && translated.slides.length > 0) {
                let tokenSlides = translated.slides;
                if (tokenId)
                    tokenSlides = _.filter(translated.slides, s => s.accessGroups.includes(tokenId));
                setSlide(tokenSlides[0]);
                setSlides(tokenSlides);
                slideChanged(0, true);
                setStop(true);
                loadTemplates();

                if (tokenSlides.length === 0)
                    return;
                //console.log("SlideShowContent.tsx::244 => tokenSlides", tokenSlides);

                //xapi stuff
                const state: DocumentJson = {
                    slideShowId: slideShow.id,
                    slideId: tokenSlides[0].id,
                    accessTokenId: tokenId,
                };
                if (xapi) {
                    xapi.xapiState(activityId, '/xapiSlidesSession', state, 'started');
                }
            }
        }
    }, [slideShow, slideChanged, resize, width, height, lng, tokenId, slides, activityId, xapi, stop]);

    const dim = calcAvailableSize(width, height - 60);
    let slideHeight = isMobileView ? height - height * 0.08 : width * 9 / 16 + 50;
    if (slideHeight > 850)
        slideHeight = 850;
    const bgStyle = calculateSlideBackgroundConfigurationStyle(viewMode, cWidth, cHeight, slide?.background ?? slideShow?.background);
    const fcol = ((slide?.background ?? slideShow?.background)?.foregroundColor) ?? "#000000";
    const fg = fadeColor({ theme }, fcol, 70);

    const playAudio = React.useMemo(() => () => {
        const ele = document.getElementById("nextpage") as HTMLAudioElement;
        if (ele) {
            ele.playbackRate = 1.5;
            ele.play();
        }
    }, []);

    const switchSlide = React.useMemo(() => (idx: number) => {
        playAudio();
        slideChanged(idx);
        setSlide(slides[idx]);

        // xapi stuff
        const state: DocumentJson = {
            slideShowId,
            slideId: slides[idx].id,
            accessTokenId: tokenId,
        };
        const verb = idx < slides.length - 1 ? 'experienced' : 'finished';
        if (xapi)
            xapi.xapiState(activityId, '/xapiSlidesSession', state, verb);
    }, [playAudio, slideChanged, slides, slideShowId, tokenId, activityId, xapi]);

    const goToNext = React.useMemo(() => (idx: number) => () => {
        if (idx > slides.length - 1) {
            return;
        }
        switchSlide(idx);
    }, [switchSlide, slides]);

    const goToPrevious = React.useMemo(() => (idx: number) => () => {
        if (idx < 0) {
            return;
        }
        switchSlide(idx);
    }, [switchSlide]);

    const swipe = defineSwipe({ swipeDistance: 50 });
    const template = slide && _.find(templates, ['id', slide.templateId]);
    const idx = _.findIndex(slides, s => s.id === slide?.id);


    const { hasCompare, hasCompareAndVideo } = React.useMemo(() => {
        const parts = slide?.parts ?? [];
        //console.log('parts', parts);

        let hasCompare = false;
        let hasVideo = false;

        for (const part of parts) {
            if (part === null)
                continue;
            if (part.partType === "Compare") hasCompare = true;
            if (
                part.media?.mediaType === "video/mp4" ||
                part.subPart?.media?.mediaType === "video/mp4" ||
                part.media?.mediaType === "audio/mpeg" ||
                part.subPart?.media?.mediaType === "audio/mpeg"
            ) {
                hasVideo = true;
            }
            if (hasCompare && hasVideo) break;
        }

        return { hasCompare, hasCompareAndVideo: hasCompare && hasVideo };
    }, [slide?.parts]);



    return (
        <>
            < audio preload="true" id="nextpage" >
                <source src="/nextpage.mp3" />
            </audio >
            <T.SlideShowContainer isMobile={isMobile} isMobileView={isMobileView} style={{ overflow: "unset", height: contentWidth * 9 / 16 + 50, userSelect: "none" }}>
                <T.SlideContainer
                    isMobile={isMobile}
                    isMobileView={isMobileView}
                    dim={dim}
                    style={bgStyle}
                    ref={setContainerRef}
                    height={slideHeight}
                >
                    <T.SlideTitleContainer height={slideHeight} isMobileView={isMobileView} isMobile={isMobile}>
                        {!isMobile &&
                            <div style={{ float: "left", marginRight: 10 }}><Icon name='bulb' size='1.2em' color={fg} marginTop={5} /></div>
                        }
                        {slide?.title}
                    </T.SlideTitleContainer>
                    <T.SlidePagination dim={dim} height={slideHeight} isMobileView={isMobileView} isMobile={isMobile}>
                        {idx + 1} / {slides.length}
                        {hasCompareAndVideo &&
                            <div style={{ float: "right", marginTop: 0, marginLeft: 10 }} >
                                <Button
                                    tooltip={{ tooltipText: allowPointerEvents ? ("unlock slider") : ("lock slider") }}
                                    icon={allowPointerEvents ? "unlock" : "lock"}
                                    onClick={() => setAllowPointerEvents(!allowPointerEvents)}
                                    kind={allowPointerEvents ? "halfTransparentButton" : "transparentButton"}
                                    iconColor={allowPointerEvents ? "@accentRed" : "@accentBlack"}
                                />
                            </div>
                        }
                    </T.SlidePagination>
                    <Swipeable config={swipe} onSwipeLeft={hasCompare ? undefined : goToNext(idx + 1)} onSwipeRight={hasCompare ? undefined : goToPrevious(idx - 1)}>
                        <T.SlideInsideGrid dim={dim} background={(slide?.background ?? slideShow?.background)?.contentBackgroundColor ?? "#ffffff64"} className="SlideInsideGrid"
                            style={{ overflowY: "auto", marginTop: 2 }}
                        >
                            <Slide
                                lng={lng}
                                height={cHeight * 0.88}
                                slide={slide}
                                template={template}
                                viewMode={viewMode}
                                allowPointerEvents={allowPointerEvents}
                            />
                        </T.SlideInsideGrid>
                    </Swipeable>
                    {!isMobile && idx > 0 &&
                        <T.SlideNavigationLeft dim={dim} position='left' onClick={goToPrevious(idx - 1)}  >
                            <Icon name='chevron left' size='50px' color={fg} />
                        </T.SlideNavigationLeft>
                    }
                    {!isMobile && idx < slides.length - 1 &&
                        <T.SlideNavigationRight dim={dim} position='right' onClick={goToNext(idx + 1)} >
                            <Icon name='chevron right' size='50px' color={fg} />
                        </T.SlideNavigationRight>
                    }
                    <T.NavigationDots dim={dim} style={{ color: fg, textAlign: "center" }} className='DOTS'>
                        {_.map(slides, (s, index) => {
                            const select = () => switchSlide(index);
                            return <T.NavigationDot onClick={select} style={{ pointerEvents: index === idx ? "none" : "all", transform: `translateY(2px) scale(${index === idx ? 1.4 : 1})` }} key={index}>
                                <Icon
                                    name={index === idx ? 'circle dot' : 'circle'}
                                    color={fg}
                                />
                            </T.NavigationDot>;
                        })}
                    </T.NavigationDots>
                </T.SlideContainer>
                {hasCompareAndVideo && !allowPointerEvents &&
                    <div style={{ position: "absolute", top: 5, right: 13 }}>
                        <Tooltip tooltipText={t("to control the media you have to lock the slider")} noMargin notInline>
                            <span>
                                <Icon name="questionark circle" size={24} color='@accentRed' />
                            </span>
                        </Tooltip>
                    </div>
                }
            </T.SlideShowContainer>
        </>
    );
}
export default SlideShowContent;